import { trackConversion } from '@/lib/observability/track'

/**
 * The single definition of a "Start your club" CTA click — the top of the
 * club-signup funnel. Every surface that offers the club-signup entry point
 * routes its click through here, so there is one event and one closed list of
 * sources, with no per-button handlers to drift.
 *
 * `source` names the SURFACE, never the visitor: it is how we learn which page
 * actually sells. Keeping the union closed means a typo can't silently open a
 * new bucket in GA4.
 */
export type StartClubCtaSource =
  | 'header'
  | 'drawer'
  | 'home_hero'
  | 'home_closing'
  | 'for_clubs_hero'
  | 'for_clubs_closing'
  | 'pricing_hero'
  | 'pricing_closing'
  | 'pricing_plan_card'
  | 'solutions_hero'
  | 'solutions_closing'
  | 'about_closing'

/** Report one CTA click. `plan` is the static pricing-tier name where the CTA
 *  sits inside a plan card (Free / Beginner / …) — a catalogue label, not
 *  anything about the visitor. */
export function trackStartClubCta(source: StartClubCtaSource, plan?: string): void {
  trackConversion('cta_start_club_click', { source, plan })
}

/** onClick for a start-club link that is NOT a ButtonLink (the nav rows render a
 *  plain <Link>). `source` is optional because the nav CTA is per-mode: player
 *  mode offers "Join free" instead, which is a different funnel and must not
 *  count here. `after` preserves the host's own handler (e.g. close the drawer). */
export function startClubCtaHandler(
  source: StartClubCtaSource | undefined,
  after?: () => void,
): () => void {
  return () => {
    if (source) trackStartClubCta(source)
    after?.()
  }
}
